import { useEffect, useRef, useState } from 'react';

import { AppIcon, type AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, type SxProps, type Theme, Tooltip } from '@mui/material';

const FEEDBACK_MS = 2000;

type Status = 'idle' | 'done' | 'failed';

export type ActionIconButtonProps = Readonly<{
  id?: string;
  iconName: AppIcons;
  label: string;
  doneLabel: string;
  failedLabel: string;
  /** Resolves true on success; the tooltip reports the outcome. */
  onAction: () => Promise<boolean>;
}>;

/** An icon button whose tooltip confirms whether its action worked. */
export const ActionIconButton = ({
  id,
  iconName,
  label,
  doneLabel,
  failedLabel,
  onAction,
}: ActionIconButtonProps) => {
  const [status, setStatus] = useState<Status>('idle');
  const [isHovered, setIsHovered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleClick = async () => {
    const succeeded = await onAction().catch(() => false);

    setStatus(succeeded ? 'done' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus('idle'), FEEDBACK_MS);
  };

  const title = { idle: label, done: doneLabel, failed: failedLabel }[status];

  return (
    <Tooltip
      title={title}
      placement="top"
      arrow
      open={isHovered || status !== 'idle'}
      disableHoverListener
    >
      <Box
        id={id}
        component="button"
        type="button"
        aria-label={label}
        sx={buttonSx}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AppIcon iconName={iconName} diameter="16px" color="currentColor" />
      </Box>
    </Tooltip>
  );
};

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xs'],
  border: 'none',
  borderRadius: backrs.radius['radius-sm'],
  background: 'none',
  cursor: 'pointer',
  color: backrs.colors['Gray/500'],
  '&:hover': {
    color: backrs.colors['Brand/500'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary-hover'],
  },
});
